import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
} from '@mui/material'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { selectLoadsFromVehicles } from '@/features/domain/vehicle'

import { useController, type BalanceBy as BalanceByValue } from '../../hooks/useController'
import { useTexts } from '../../useTexts'

// Cap the Select-a-type list at 5 visible items, then scroll. MUI MenuItem
// is ~36px tall by default; 5 * 36 + 4px for the list's vertical padding.
const SELECT_MAX_HEIGHT = 5 * 36 + 4

interface Props {
  disabled?: boolean
}

export function BalanceBy({ disabled = false }: Props) {
  const texts = useTexts()
  const loads = useSelector(selectLoadsFromVehicles)
  const {
    data: { balanceBy, loadType, submitAttempted },
    updateData,
  } = useController()

  const balanceByError = submitAttempted && balanceBy === ''
  const loadTypeError = submitAttempted && balanceBy === 'loadType' && loadType === ''

  const handleBalanceByChange = useCallback(
    (event: SelectChangeEvent<BalanceByValue | ''>) => {
      const value = event.target.value as BalanceByValue
      updateData({ balanceBy: value, loadType: value === 'loadType' ? loadType : '' })
    },
    [loadType, updateData],
  )

  const handleLoadTypeChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      updateData({ loadType: event.target.value })
    },
    [updateData],
  )

  const renderBalanceByOption = (primary: string, secondary: string) => (
    <Stack>
      <Typography>{primary}</Typography>
      <Typography variant="caption" color="text.secondary">
        {secondary}
      </Typography>
    </Stack>
  )

  return (
    <Stack spacing={1.5}>
      <FormControl size="small" fullWidth error={balanceByError} disabled={disabled}>
        <InputLabel id="route-optimization-balance-by-label">{texts.balanceByLabel}</InputLabel>
        <Select
          labelId="route-optimization-balance-by-label"
          label={texts.balanceByLabel}
          value={balanceBy}
          onChange={handleBalanceByChange}
          SelectDisplayProps={{
            'data-trackid': 'route-optimization__balance-by',
            'data-testid': 'route-optimization__balance-by',
          }}
          renderValue={value => {
            switch (value) {
              case 'orders':
                return texts.balanceByOrders
              case 'workingTime':
                return texts.balanceByWorkingTime
              case 'distance':
                return texts.balanceByDistance
              case 'loadType':
                return texts.balanceByLoadType
              default:
                return ''
            }
          }}
        >
          <MenuItem value="orders">
            {renderBalanceByOption(texts.balanceByOrders, texts.balanceByOrdersDescription)}
          </MenuItem>
          <MenuItem value="workingTime">
            {renderBalanceByOption(
              texts.balanceByWorkingTime,
              texts.balanceByWorkingTimeDescription,
            )}
          </MenuItem>
          <MenuItem value="distance">
            {renderBalanceByOption(texts.balanceByDistance, texts.balanceByDistanceDescription)}
          </MenuItem>
          <MenuItem value="loadType">
            {renderBalanceByOption(texts.balanceByLoadType, texts.balanceByLoadTypeDescription)}
          </MenuItem>
        </Select>
        <FormHelperText>
          {balanceByError ? texts.balanceByRequired : texts.balanceByHelper}
        </FormHelperText>
      </FormControl>

      {balanceBy === 'loadType' && (
        <FormControl size="small" fullWidth error={loadTypeError} disabled={disabled}>
          <InputLabel id="route-optimization-load-type-label">{texts.loadTypeLabel}</InputLabel>
          <Select
            labelId="route-optimization-load-type-label"
            label={texts.loadTypeLabel}
            value={loadType}
            onChange={handleLoadTypeChange}
            SelectDisplayProps={{
              'data-trackid': 'route-optimization__load-type',
              'data-testid': 'route-optimization__load-type',
            }}
            MenuProps={{
              PaperProps: { sx: { maxHeight: SELECT_MAX_HEIGHT } },
            }}
          >
            {loads.map(load => (
              <MenuItem key={load} value={load}>
                {load}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {loadTypeError ? texts.loadTypeRequired : texts.loadTypeDescription}
          </FormHelperText>
        </FormControl>
      )}
    </Stack>
  )
}
